import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  TablePagination,
  Divider,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import Layout from './Layout';
import CertificateLink from './CertificateLink';
import { formatDateTime } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { useOpenOrdersCount } from '../context/OpenOrdersCountContext';
import { ROLES } from '../constants/roles';
import { ORDER_STATUSES, ORDER_GROUP_STATUSES, DIAMOND_TYPES, formatGradeShort } from '../constants/appConstants';
import axios from 'axios';
import { getNonCertLotDisplayTitle } from '../utils/nonCertLotDisplay';
import { formatCurrency } from '../utils/currency';
import {
  getItemDisplayPrice,
  getItemPricePerCt as getItemPricePerCtDisplay,
} from '../utils/orderItemDisplay';
import { useNavigate } from 'react-router-dom';
import { TABLE_ROWS_PREF_KEYS, pickRowsPerPageFromPrefs, saveTableRowsPreference } from '../utils/tablePagePreferences';
import { pickOrdersStatusFilterFromPrefs, saveOrdersStatusFilterPreference, resolveDefaultOrdersStatusFilter } from '../utils/ordersFilterPreferences';
import { useTranslation } from 'react-i18next';
import AutocompleteField from './inputs/AutocompleteField';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useFeedback } from '../context/FeedbackContext';
import ClientTagsRow from './clients/ClientTagsRow';
import { getClientTabPath } from '../utils/navigationReturn';
import OrderItemNotes from './OrderItemNotes';

const GROUP_STATUS_OPTIONS = [
  ORDER_GROUP_STATUSES.OPEN,
  ORDER_GROUP_STATUSES.READY,
  ORDER_GROUP_STATUSES.MEMO,
  ORDER_GROUP_STATUSES.PAID,
  ORDER_GROUP_STATUSES.CANCELLED,
];

/**
 * @param {string}  [clientId]  – when provided, pre-filters by this clientUserId and hides the client filter/column
 * @param {boolean} [embedded]  – when true, omits Layout wrapper and page title (for use inside tabs)
 */
const OrdersList = ({ clientId, embedded = false }) => {
  const navigate = useNavigate();
  const { isInternal, userRole, loading: authLoading } = useAuth();
  const { refreshOpenOrdersCount } = useOpenOrdersCount();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { confirm, showError } = useFeedback();

  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState(clientId || '');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 20 : 50);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const allowed = isMobile ? [10, 20, 50] : [10, 20, 50, 100];
    const fallback = isMobile ? 20 : 50;
    setRowsPerPage((prev) => (allowed.includes(prev) ? prev : fallback));
  }, [isMobile]);

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelItems, setCancelItems] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);
  const { loading: clientCancelLoading, run: runClientCancel } = useAsyncAction();

  const isManager = isInternal && userRole === ROLES.MANAGER;

  const getGroupStatusLabel = (status) => {
    const labels = {
      [ORDER_GROUP_STATUSES.OPEN]: t('status.open'),
      [ORDER_GROUP_STATUSES.READY]: t('status.ready'),
      [ORDER_GROUP_STATUSES.MEMO]: t('status.memo'),
      [ORDER_GROUP_STATUSES.PAID]: t('status.paid'),
      [ORDER_GROUP_STATUSES.CANCELLED]: t('status.cancelled'),
    };
    return labels[status] || status;
  };

  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  const statusOptions = GROUP_STATUS_OPTIONS.map((s) => ({ value: s, label: getGroupStatusLabel(s) }));

  const handleStatusFilterChange = (next) => {
    const nextStatuses = next || [];
    setStatusFilter(nextStatuses);
    setPage(0);
    saveOrdersStatusFilterPreference(nextStatuses).catch(() => {});
  };

  // Keep clientFilter in sync if the clientId prop changes
  useEffect(() => {
    setClientFilter(clientId || '');
    setPage(0);
  }, [clientId]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(statusFilter.length > 0 && { status: statusFilter.join(',') }),
        ...(clientFilter && { clientUserId: clientFilter }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
      };
      const res = await axios.get('/api/orders', { params });
      const groups = res.data.orders || [];

      if (!isInternal && groups.length > 0) {
        const withItems = await Promise.all(groups.map(async (g) => {
          try {
            const detail = await axios.get(`/api/orders/${g._id}`);
            return { ...g, _itemsPreview: detail.data.items || [] };
          } catch {
            return { ...g, _itemsPreview: [] };
          }
        }));
        setOrders(withItems);
      } else {
        setOrders(groups);
      }
      setTotal(res.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch order groups:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, clientFilter, dateFrom, dateTo, isInternal]);

  useEffect(() => {
    if (!preferencesLoaded) return;
    fetchOrders();
  }, [fetchOrders, preferencesLoaded]);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    setPreferencesLoaded(false);
    (async () => {
      try {
        const { data } = await axios.get('/api/users/preferences');
        if (cancelled) return;
        const allowed = isMobile ? [10, 20, 50] : [10, 20, 50, 100];
        const fallback = isMobile ? 20 : 50;
        setRowsPerPage(pickRowsPerPageFromPrefs(data || {}, TABLE_ROWS_PREF_KEYS.orders, allowed, fallback));
        const defaultStatuses = resolveDefaultOrdersStatusFilter(isInternal, clientId);
        setStatusFilter(pickOrdersStatusFilterFromPrefs(data || {}, defaultStatuses));
      } catch (_) {
        if (!cancelled) {
          setStatusFilter(resolveDefaultOrdersStatusFilter(isInternal, clientId));
        }
      } finally {
        if (!cancelled) setPreferencesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isInternal, clientId, isMobile]);

  useEffect(() => {
    if (isInternal && !clientId) fetchClients();
  }, [isInternal, clientId]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`/api/users?role=${encodeURIComponent(ROLES.CLIENT)}`);
      setClients((response.data || []).filter((u) => !u.disabled));
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const openOrderDetail = (group, auditTab = false) => {
    const ids = orders.map((o) => o._id);
    const listState = { ids, index: ids.indexOf(group._id), ...(auditTab ? { tab: 1 } : {}) };
    if (clientId) {
      navigate(getClientTabPath(clientId, 'orders', group._id), { state: listState });
      return;
    }
    navigate(`/orders/${group._id}`, { state: listState });
  };

  const canClientCancel = (group) => {
    return !isInternal && group.status === ORDER_GROUP_STATUSES.OPEN;
  };

  const handleClientCancel = async (groupId) => {
    if (!(await confirm(t('orders.confirmCancelOrder')))) return;
    await runClientCancel(async () => {
      try {
        await axios.post(`/api/orders/${groupId}/client-cancel`);
        await fetchOrders();
        refreshOpenOrdersCount();
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to cancel order');
      }
    }, `client-cancel-${groupId}`);
  };

  const openCancelDialog = async (type, groupId) => {
    if (type !== 'group') return;
    setCancelTarget({ type, groupId });
    setCancelDialogOpen(true);
    try {
      const res = await axios.get(`/api/orders/${groupId}`);
      const items = (res.data.items || []).filter(
        (i) => i.status !== ORDER_STATUSES.CANCELED && i.status !== ORDER_STATUSES.EXPIRED
      );
      setCancelItems(items.map((i) => ({ ...i, returnToInventory: true })));
    } catch {
      setCancelItems([]);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelTarget(null);
    setCancelItems([]);
  };

  const toggleReturnToInventory = (itemId) => {
    setCancelItems((prev) =>
      prev.map((i) => (i._id === itemId ? { ...i, returnToInventory: !i.returnToInventory } : i))
    );
  };

  const toggleAllReturn = (checked) => {
    setCancelItems((prev) => prev.map((i) => ({ ...i, returnToInventory: checked })));
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      if (cancelTarget.type === 'group') {
        const returnItemIds = cancelItems.filter((i) => i.returnToInventory).map((i) => i._id);
        await axios.patch(`/api/orders/${cancelTarget.groupId}/status`, {
          status: ORDER_GROUP_STATUSES.CANCELLED,
          returnItemIds,
        });
      } else if (cancelTarget.type === 'item') {
        const item = cancelItems[0];
        const returnToInventory = item?.returnToInventory ?? false;
        if (item && (item.status === ORDER_STATUSES.APPROVED || item.status === ORDER_STATUSES.REQUESTED)) {
          await axios.post(
            `/api/orders/${cancelTarget.groupId}/items/${cancelTarget.itemId}/cancel`,
            { returnToInventory }
          );
        } else {
          await axios.patch(
            `/api/orders/${cancelTarget.groupId}/items/${cancelTarget.itemId}/status`,
            { status: ORDER_STATUSES.CANCELED, returnToInventory }
          );
        }
      }
      await fetchOrders();
      refreshOpenOrdersCount();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to cancel');
    } finally {
      setCancelLoading(false);
      closeCancelDialog();
    }
  };

  const getGroupStatusColor = (status) => {
    switch (status) {
      case ORDER_GROUP_STATUSES.OPEN: return 'primary';
      case ORDER_GROUP_STATUSES.READY: return 'success';
      case ORDER_GROUP_STATUSES.MEMO: return 'warning';
      case ORDER_GROUP_STATUSES.PAID: return 'success';
      case ORDER_GROUP_STATUSES.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getItemsSummary = (group) => {
    const tot = group.totalItems || 0;
    const ready = group.readyCount || 0;
    const cancelled = group.cancelledCount || 0;
    const deliveredCt = group.deliveredCount ?? 0;
    const paid = group.paidCount || 0;
    if (tot === 0) return '';
    const parts = [];
    if (paid > 0) parts.push(`${paid} ${t('status.paid')}`);
    if (deliveredCt > 0) parts.push(`${deliveredCt} ${t('status.memo')}`);
    if (ready > 0) parts.push(`${ready} ${t('status.ready')}`);
    if (cancelled > 0) parts.push(`${cancelled} ${t('status.cancelled')}`);
    const pending = tot - ready - cancelled - deliveredCt - paid;
    if (pending > 0) parts.push(`${pending} ${t('orders.pendingCount')}`);
    return parts.join(', ');
  };

  const getItemStatusColor = (status) => {
    switch (status) {
      case ORDER_STATUSES.REQUESTED: return 'warning';
      case ORDER_STATUSES.APPROVED: return 'info';
      case ORDER_STATUSES.READY: return 'primary';
      case ORDER_STATUSES.MEMO: return 'warning';
      case ORDER_STATUSES.PAID: return 'success';
      case ORDER_STATUSES.CANCELED: return 'default';
      case ORDER_STATUSES.EXPIRED: return 'default';
      default: return 'default';
    }
  };

  const getItemStatusLabel = (status) => {
    if (!status) return '';
    const labels = {
      [ORDER_STATUSES.REQUESTED]: t('status.requested'),
      [ORDER_STATUSES.APPROVED]: t('status.approved'),
      [ORDER_STATUSES.READY]: t('status.ready'),
      [ORDER_STATUSES.CANCELED]: t('status.cancelled'),
      [ORDER_STATUSES.EXPIRED]: t('status.expired'),
      [ORDER_STATUSES.MEMO]: t('status.memo'),
      [ORDER_STATUSES.PAID]: t('status.paid'),
    };
    return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getGroupClientName = (group) => {
    const u = group.clientUser;
    if (u) return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
    return group.company?.name || '—';
  };

  const getGroupDate = (group) => group.createdAt ? formatDateTime(group.createdAt) : '—';

  const getGroupOrderNumber = (group) => '#' + group._id.slice(-6).toUpperCase();

  const getCreatedByName = (group) => {
    const u = group.createdBy;
    if (!u) return '—';
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
  };

  const getItemRef = (item) => {
    if (item.type === DIAMOND_TYPES.NON_CERTIFIED) return getNonCertLotDisplayTitle(item.lot);
    return (item.diamondSnapshot?.certificateNumber ?? item.diamond?.certificateNumber) ?? '—';
  };

  const getItemLab = (item) => item?.diamondSnapshot?.lab ?? item?.diamond?.lab;

  const getItemDetails = (item) => {
    if (item.type === DIAMOND_TYPES.CERTIFIED) {
      const d = item.diamondSnapshot || item.diamond;
      if (!d) return getItemRef(item);
      return [d.shape, d.carat != null && `${d.carat} ct`, d.diamondType, d.color, d.clarity, d.lab].filter(Boolean).join(' · ');
    }
    if (item.weight != null) return `${item.weight} ct`;
    return '—';
  };

  const groupCurrencyCode = (group) => group.totalPriceCurrency || 'USD';

  const getItemPrice = (item) => getItemDisplayPrice(item);

  const getItemPricePerCt = (item) => getItemPricePerCtDisplay(item);

  const getOrderAmountDue = (group) => {
    const items = group._itemsPreview || [];
    if (items.length === 0) return null;
    const unpaidStatuses = [ORDER_STATUSES.REQUESTED, ORDER_STATUSES.APPROVED, ORDER_STATUSES.READY, ORDER_STATUSES.MEMO];
    const due = items
      .filter((i) => unpaidStatuses.includes(i.status))
      .reduce((sum, i) => sum + (i.finalPrice || 0), 0);
    return Math.round(due * 100) / 100;
  };

  const getItemTypeLabel = (item) =>
    item.type === DIAMOND_TYPES.NON_CERTIFIED ? t('cart.nonCertified') : t('cart.certified');

  // When embedded inside a client page, don't show the client column/link (already in context)
  const showClientColumn = isInternal && !clientId;

  const content = (
    <Box>
      {/* ──── Page title (standalone mode only) ──── */}
      {!embedded && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {isInternal ? t('orders.title') : t('orders.myOrders')}
          </Typography>
        </Box>
      )}

      {/* ──── Filters ──── */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            size="small"
            label={t('orders.fromDate')}
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
          />
          <TextField
            size="small"
            label={t('orders.toDate')}
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
          />
          <AutocompleteField
            multiple
            label={t('orders.status')}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            options={statusOptions}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            placeholder={t('orders.all')}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
          />
          {isInternal && !clientId && (
            <AutocompleteField
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
              options={clients}
              value={clientFilter}
              onChange={(next) => { setClientFilter(next || ''); setPage(0); }}
              label={t('orders.filterByClient')}
              getOptionLabel={(u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.email ? ` (${u.email})` : '')}
              getOptionValue={(u) => u._id}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
            />
          )}
        </Box>
      </Paper>

      {/* ──── Main content ──── */}
      {loading ? (
        <Typography sx={{ py: 3 }}>{t('orders.loading')}</Typography>
      ) : (!isInternal || isMobile) ? (
        /* ──── Card layout for clients (always) and internal mobile ──── */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {orders.map((group) => (
            <Card key={group._id} elevation={2}>
              <CardContent sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                      {getGroupOrderNumber(group)}
                    </Typography>
                    <Chip
                      label={getGroupStatusLabel(group.status)}
                      color={getGroupStatusColor(group.status)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {getGroupDate(group)}
                  </Typography>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap" mb={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {group.totalItems || group.itemCount} item{(group.totalItems || group.itemCount) !== 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {t('orders.total')} {formatCurrency(group.totalPrice || 0, groupCurrencyCode(group))}
                  </Typography>
                  {getOrderAmountDue(group) != null && getOrderAmountDue(group) > 0 && (
                    <Chip
                      label={`Due: ${formatCurrency(getOrderAmountDue(group), groupCurrencyCode(group))}`}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {getOrderAmountDue(group) != null && getOrderAmountDue(group) === 0 && group.status !== ORDER_GROUP_STATUSES.CANCELLED && (
                    <Chip label={t('orders.fullyPaid')} color="success" size="small" variant="outlined" />
                  )}
                  {showClientColumn && (
                    <Typography variant="body2" color="text.secondary">
                      Client:{' '}
                      {group.clientUser?._id ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => { e.stopPropagation(); navigate(getClientTabPath(group.clientUser._id, 'items')); }}
                          sx={{ verticalAlign: 'baseline' }}
                        >
                          {getGroupClientName(group)}
                        </Link>
                      ) : getGroupClientName(group)}
                    </Typography>
                  )}
                </Box>
                {showClientColumn && (group.clientUser?.clientTags || []).length > 0 && (
                  <ClientTagsRow tags={group.clientUser.clientTags} maxVisible={4} sx={{ mb: 1 }} />
                )}
                {getItemsSummary(group) && (
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    {getItemsSummary(group)}
                  </Typography>
                )}
              </CardContent>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                {(group._itemsPreview || []).length > 0 ? (
                  group._itemsPreview.map((item, idx) => {
                    const isCert = item.type === DIAMOND_TYPES.CERTIFIED;
                    const d = item.diamondSnapshot || item.diamond;
                    const lot = item.lot;
                    return (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          mb: idx < group._itemsPreview.length - 1 ? 1 : 0,
                          borderRadius: 1.5,
                          backgroundColor: 'action.hover',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isCert ? (
                              <>
                                <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" sx={{ fontSize: '0.95rem', fontWeight: 600 }} />
                                {d && (
                                  <Box sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                                      {[d.shape, d.carat != null && `${d.carat} ct`, d.diamondType, d.color, d.clarity, d.lab].filter(Boolean).join(' · ')}
                                    </Typography>
                                    {(d.cut || d.polish || d.symmetry) && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, fontSize: '0.8rem' }}>
                                        Cut {formatGradeShort(d.cut) || '—'} · Sym {formatGradeShort(d.symmetry) || '—'} · Pol {formatGradeShort(d.polish) || '—'}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </>
                            ) : (
                              <>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                                  {getItemRef(item)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                                  {item.weight != null ? `${item.weight} ct` : ''}
                                  {lot?.measurement ? ` · ${lot.measurement}` : ''}
                                </Typography>
                              </>
                            )}
                            <OrderItemNotes item={item} sx={{ mt: 0.25 }} />
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                            <Box textAlign="right">
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {getItemPricePerCt(item)}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.95rem' }}>
                                {getItemPrice(item)}
                              </Typography>
                            </Box>
                            <Chip label={getItemStatusLabel(item.status)} color={getItemStatusColor(item.status)} size="small" />
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, cursor: 'pointer' }} onClick={() => openOrderDetail(group)}>
                    {t('orders.clickToView')}
                  </Typography>
                )}
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={1} sx={{ px: 2, py: 1.5 }}>
                <Button size="small" variant="outlined" onClick={() => openOrderDetail(group)} startIcon={<ViewIcon />}>
                  {t('orders.details')}
                </Button>
                {canClientCancel(group) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleClientCancel(group._id)}
                    disabled={clientCancelLoading}
                    startIcon={<CancelIcon />}
                  >
                    {t('orders.cancelOrder')}
                  </Button>
                )}
                {isInternal && isManager && [ORDER_GROUP_STATUSES.OPEN, ORDER_GROUP_STATUSES.READY, ORDER_GROUP_STATUSES.MEMO].includes(group.status) && (
                  <Tooltip title={t('orders.cancelOrder')}>
                    <IconButton size="small" color="error" onClick={() => openCancelDialog('group', group._id)}
                      sx={{ border: '1px solid', borderColor: 'error.main' }}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Card>
          ))}
          {orders.length === 0 && (
            <Paper sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('orders.noOrdersFound')}</Typography>
            </Paper>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setRowsPerPage(next);
              setPage(0);
              saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.orders, next).catch(() => {});
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Box>
      ) : (
        /* ──── Desktop table layout (internal, non-mobile) ──── */
        <>
          <TableContainer component={Paper} sx={{ maxHeight: embedded ? 'max(200px, calc(100dvh - 380px))' : 'max(200px, calc(100dvh - 280px))', overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('orders.orderNumber')}</TableCell>
                  {showClientColumn && <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('orders.client')}</TableCell>}
                  <TableCell align="center" sx={{ backgroundColor: 'background.paper' }}>{t('orders.items')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('orders.totalPrice')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('common.status')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('orders.itemsStatus')}</TableCell>
                  {isInternal && <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('orders.createdBy')}</TableCell>}
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('common.date')}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: 'background.paper' }}>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((group) => (
                  <TableRow
                    key={group._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openOrderDetail(group)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {getGroupOrderNumber(group)}
                      </Typography>
                    </TableCell>
                    {showClientColumn && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {group.clientUser?._id ? (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate(getClientTabPath(group.clientUser._id, 'items'))}
                          >
                            {getGroupClientName(group)}
                          </Link>
                        ) : getGroupClientName(group)}
                        {(group.clientUser?.clientTags || []).length > 0 && (
                          <ClientTagsRow tags={group.clientUser.clientTags} maxVisible={3} sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">{group.totalItems || group.itemCount}</TableCell>
                    <TableCell>{formatCurrency(group.totalPrice || 0, groupCurrencyCode(group))}</TableCell>
                    <TableCell>
                      <Chip
                        label={getGroupStatusLabel(group.status)}
                        color={getGroupStatusColor(group.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {getItemsSummary(group)}
                      </Typography>
                    </TableCell>
                    {isInternal && <TableCell>{getCreatedByName(group)}</TableCell>}
                    <TableCell>{getGroupDate(group)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        <Tooltip title={t('orders.viewDetails')}>
                          <IconButton size="small" onClick={() => openOrderDetail(group)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isInternal && (
                          <Tooltip title={t('orders.auditLog')}>
                            <IconButton size="small" onClick={() => openOrderDetail(group, true)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isManager && [ORDER_GROUP_STATUSES.OPEN, ORDER_GROUP_STATUSES.READY, ORDER_GROUP_STATUSES.MEMO].includes(group.status) && (
                          <Tooltip title={t('orders.cancelOrder')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openCancelDialog('group', group._id)}
                              sx={{ border: '1px solid', borderColor: 'error.main' }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={showClientColumn ? 9 : 8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('orders.noOrdersFound')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setRowsPerPage(next);
              setPage(0);
              saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.orders, next).catch(() => {});
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </>
      )}

      {/* ──── Cancel Confirmation Dialog ──── */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" color="error.main">
            {cancelTarget?.type === 'group' ? t('orders.cancelEntireOrder') : t('orders.cancelItem')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {cancelTarget?.type === 'group'
              ? t('orders.cancelOrderBody')
              : t('orders.cancelItemBody')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {cancelItems.length > 0 ? (
            <>
              {cancelTarget?.type === 'group' && cancelItems.length > 1 && (
                <Box mb={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={cancelItems.every((i) => i.returnToInventory)}
                        indeterminate={cancelItems.some((i) => i.returnToInventory) && !cancelItems.every((i) => i.returnToInventory)}
                        onChange={(e) => toggleAllReturn(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        {t('orders.selectDeselectAll')}
                      </Typography>
                    }
                  />
                  <Divider />
                </Box>
              )}
              {cancelItems.map((item) => (
                <Box
                  key={item._id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box flex={1}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography component="span" variant="body2" fontWeight="bold">{getItemTypeLabel(item)} — </Typography>
                      {item.type === DIAMOND_TYPES.CERTIFIED ? (
                        <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" />
                      ) : (
                        <Typography component="span" variant="body2" fontWeight="bold">{getItemRef(item)}</Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {getItemDetails(item)} | {getItemPrice(item)} | Status: {getItemStatusLabel(item.status)}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.returnToInventory}
                        onChange={() => toggleReturnToInventory(item._id)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">{t('orders.returnToInventory')}</Typography>
                    }
                    sx={{ ml: 2 }}
                  />
                </Box>
              ))}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>{t('orders.noActiveItems')}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeCancelDialog} disabled={cancelLoading}>
            {t('orders.goBack')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmCancel}
            disabled={cancelLoading || cancelItems.length === 0}
          >
            {cancelLoading ? t('orders.cancelling') : cancelTarget?.type === 'group' ? t('orders.cancelOrder') : t('orders.cancelItem')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
        {content}
      </Container>
    </Layout>
  );
};

export default OrdersList;
